import { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// The form pulls in react-hook-form + zod; loading it on demand keeps them out
// of the main bundle every page has to download and parse before it responds.
const loadContactForm = () => import("@/components/ContactForm");
const ContactForm = lazy(loadContactForm);

interface ContactDialogContextValue {
  openContactDialog: () => void;
}

// Default is a no-op so components using CTAButton still render outside the
// provider (e.g. the prerender in entry-server.tsx).
const ContactDialogContext = createContext<ContactDialogContextValue>({
  openContactDialog: () => {},
});

export const useContactDialog = () => useContext(ContactDialogContext);

/**
 * Site-wide "Start a project" popup. Every CTA opens this instead of
 * navigating to /contact, so visitors never lose their place on the page.
 * The form is unmounted while closed, so each open gets a fresh form and a
 * fresh formLoadedAt for the backend's too-fast-submit check.
 */
export const ContactDialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const openContactDialog = useCallback(() => setOpen(true), []);
  const value = useMemo(() => ({ openContactDialog }), [openContactDialog]);

  // Fetch the form chunk once the page is idle, so the first CTA tap opens a
  // ready form instead of waiting on the network.
  useEffect(() => {
    const prefetch = () => void loadContactForm();
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 3000);
    return () => clearTimeout(id);
  }, []);

  return (
    <ContactDialogContext.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto rounded-3xl border-border bg-card p-5 md:p-8">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 text-left pr-8">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Send className="w-4 h-4 text-primary" aria-hidden="true" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold font-heading text-foreground">
                Get a free quote
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Tell us what you need — we reply within 1 business day.
              </DialogDescription>
            </div>
          </DialogHeader>
          <Suspense fallback={<div className="min-h-[420px]" aria-busy="true" />}>
            <ContactForm />
          </Suspense>
        </DialogContent>
      </Dialog>
    </ContactDialogContext.Provider>
  );
};
